import { ReactNode, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Alert from '@/shared/components/Alert/Alert'
import Button from '@/shared/components/Button/Button'
import Modal from '@/shared/components/Modal/Modal'
import OverlayScrollbar from '@/shared/components/OverlayScrollbar/OverlayScrollbar'
import { useGoogleLocationQuery, restaurantMapUrl } from '@/features/google-location'
import BlockRenderer from '../../render/BlockRenderer'
import { RenderContext } from '../../render/context'
import { themeClassName, themeStyle } from '../../render/theme'
import { Row, RowLayout } from '../../schema/document'
import { ElementType } from '../../schema/elements'
import {
  addElement,
  addRow,
  canPlaceElement,
  createElement,
  deleteRow,
  duplicateRow,
  moveRow,
  updateRow
} from '../../utils/documentOps'
import { resolveEditorAssetUrl } from '../../utils/assets'
import { useWebsiteStore } from '../../store/useWebsiteStore'
import { useDialogOpen } from '../../hooks/useDialogOpen'
import { useEditorPlaceholders } from '../../hooks/useEditorPlaceholders'
import { useFitScale } from '../../hooks/useFitScale'
import ElementPalette from '../ElementPalette/ElementPalette'
import LayoutPicker from '../LayoutPicker/LayoutPicker'
import RowToolbar from '../RowToolbar/RowToolbar'
import styles from './EditorCanvas.module.scss'

interface EditorCanvasProps {
  device: 'desktop' | 'mobile'
}

/** How long the "row deleted" notice offers to undo, per §6 of the brief. */
const UNDO_WINDOW_MS = 10000

/*
  The widths the canvas renders AT, before it is scaled to fit the panel.

  This is the fix for the preview lying: the editor column is around 700 CSS
  pixels, which is under the site's own 768px phone breakpoint, so the desktop
  preview was drawing the phone layout and every two-column row stacked. Now the
  canvas is laid out at a real desktop width and shrunk to fit, the way every
  page builder does it, so what is on screen is what a visitor gets.
*/
const CANVAS_WIDTH = { desktop: 1200, mobile: 390 }

/**
 * The page, as it will look, with handles on it.
 *
 * It renders through the same `BlockRenderer` the published page uses; the
 * outlines, toolbars and "add element" buttons all arrive through that
 * component's slots. Nothing about the page's appearance is reimplemented here,
 * which is the only way the preview and the real thing stay in step.
 */
function EditorCanvas({ device }: EditorCanvasProps) {
  const { t } = useTranslation()
  const placeholders = useEditorPlaceholders()
  const { data: googleLocation } = useGoogleLocationQuery()

  const draft = useWebsiteStore(s => s.draft)
  const activePageId = useWebsiteStore(s => s.activePageId)
  const language = useWebsiteStore(s => s.editingLanguage)
  const selection = useWebsiteStore(s => s.selection)
  const apply = useWebsiteStore(s => s.apply)
  const select = useWebsiteStore(s => s.select)
  const undo = useWebsiteStore(s => s.undo)
  const dialogOpen = useDialogOpen()
  const setDialogOpen = useWebsiteStore(s => s.setDialogOpen)

  const [layoutPickerOpen, setLayoutPickerOpen] = useState(false)
  /*
    Ids, not the row itself. The dialog outlives the document it was opened
    over: undo while it is open used to leave it judging a row that no longer
    existed, and the pick then landed nowhere at all.
  */
  const [paletteTarget, setPaletteTarget] = useState<{
    rowId: string
    columnId: string
  } | null>(null)
  const [deletedNotice, setDeletedNotice] = useState(false)

  const scrollRef = useRef<HTMLDivElement>(null)
  const canvasWidth = CANVAS_WIDTH[device]
  const fitScale = useFitScale(scrollRef, canvasWidth)

  /*
    The notice is not a confirmation dialog. Deleting a row is cheap to undo and
    a dialog in front of every delete trains people to dismiss dialogs, so the
    row goes and the way back stays on screen for ten seconds.
  */
  useEffect(() => {
    if (!deletedNotice) return undefined
    const timeout = window.setTimeout(
      () => setDeletedNotice(false),
      UNDO_WINDOW_MS
    )
    return () => window.clearTimeout(timeout)
  }, [deletedNotice])

  const page = draft.pages.find(item => item.id === activePageId)
  /*
    Both bars, the canvas's and the panel's, come off while a dialog is up. The
    reason is in the store, next to the flag. Above the early return, because a
    hook cannot be called conditionally.
  */
  useEffect(() => {
    setDialogOpen(layoutPickerOpen || paletteTarget !== null)
  }, [layoutPickerOpen, paletteTarget, setDialogOpen])

  /*
    And put it back when the canvas goes. The flag lives in the store, which
    outlives this component, so leaving the builder with a dialog open would
    have left the template page hiding its scrollbars for the rest of the
    session.
  */
  useEffect(() => () => setDialogOpen(false), [setDialogOpen])

  if (!page) return null

  const context: RenderContext = {
    language,
    defaultLanguage: draft.defaultLanguage,
    mode: 'edit',
    resolveAssetUrl: resolveEditorAssetUrl,
    resolveHref: () => '#',
    placeholders
  }

  const renderRowChrome = (row: Row, children: ReactNode) => {
    const index = page.blocks.findIndex(item => item.id === row.id)
    const isSelected = selection?.kind === 'row' && selection.id === row.id

    return (
      /*
        `presentation` on purpose. Clicking anywhere in the row is a convenience
        for a mouse, not the only way in: the toolbar's first button does the
        same thing and is reachable by tab, which is what keeps this operable
        without a pointer (§11).
      */
      <div
        className={`${styles.rowFrame} ${isSelected ? styles.rowFrameSelected : ''}`}
        role="presentation"
        onClick={() => select({ kind: 'row', id: row.id })}
      >
        <RowToolbar
          scale={1 / fitScale}
          onSelect={() => select({ kind: 'row', id: row.id })}
          isHidden={row.hidden}
          isFirst={index === 0}
          isLast={index === page.blocks.length - 1}
          onMoveUp={() => apply(doc => moveRow(doc, page.id, row.id, -1))}
          onMoveDown={() => apply(doc => moveRow(doc, page.id, row.id, 1))}
          onDuplicate={() => apply(doc => duplicateRow(doc, page.id, row.id))}
          onToggleHidden={() =>
            apply(doc =>
              updateRow(doc, page.id, row.id, { hidden: !row.hidden })
            )
          }
          onDelete={() => {
            apply(doc => deleteRow(doc, page.id, row.id))
            select(null)
            setDeletedNotice(true)
          }}
        />
        {children}
      </div>
    )
  }

  const renderItemChrome = (item: { id: string }, children: ReactNode) => {
    const isSelected = selection?.kind === 'element' && selection.id === item.id
    return (
      <div
        className={`${styles.itemFrame} ${isSelected ? styles.itemFrameSelected : ''}`}
        role="presentation"
        onClick={event => {
          // Without this the row underneath would take the selection back.
          event.stopPropagation()
          select({ kind: 'element', id: item.id })
        }}
      >
        {/*
          The keyboard's way to the same selection. Off-screen until focused,
          then it appears where the element is, so tabbing through the canvas
          walks the blocks in the order they are on the page.
        */}
        <button
          type="button"
          className={styles.selectButton}
          onClick={event => {
            event.stopPropagation()
            select({ kind: 'element', id: item.id })
          }}
        >
          {t('WEBSITE.CANVAS.SELECT_ELEMENT')}
        </button>
        {children}
      </div>
    )
  }

  const renderColumnExtra = (row: Row, columnId: string) => (
    /*
      Counter-scaled, like the row toolbar: it sits inside the zoomed page, so
      without this it drew at the page's scale, ~70% of the app's button size.
      Editor chrome is app-sized, whatever the preview is doing. The row grows
      by the difference in edit mode only; a visitor never sees this button.
    */
    <div className={styles.columnExtra} style={{ zoom: 1 / fitScale }}>
      <Button
        label={t('WEBSITE.CANVAS.ADD_ELEMENT')}
        variant="contained"
        color="primary"
        icon="las la-plus"
        onClick={() => setPaletteTarget({ rowId: row.id, columnId })}
      />
    </div>
  )

  // The row the palette is open over, looked up fresh on every render.
  const paletteRow = paletteTarget
    ? (page.blocks.find(item => item.id === paletteTarget.rowId) ?? null)
    : null

  const handlePickElement = (type: ElementType) => {
    if (!paletteTarget) return
    apply(doc => {
      /*
        Asked again against the document as it is now, not as it was when the
        dialog opened. Between the two the owner may have undone something, and
        an element added into a column that has since taken a full-width block
        would make the page unpublishable.
      */
      const current = doc.pages
        .find(item => item.id === page.id)
        ?.blocks.find(item => item.id === paletteTarget.rowId)
      if (
        !current ||
        !canPlaceElement(current, paletteTarget.columnId, type).allowed
      ) {
        return doc
      }
      /*
        A map shows the venue's Google place, so one dropped from the palette
        starts on the connected place rather than waiting for one there is.
        From then on it follows the connection (`syncRestaurantMap`).
      */
      return addElement(
        doc,
        page.id,
        paletteTarget.columnId,
        createElement(type, language, restaurantMapUrl(googleLocation))
      )
    })
    setPaletteTarget(null)
  }

  const handlePickLayout = (layout: RowLayout) => {
    apply(doc => addRow(doc, page.id, layout))
    setLayoutPickerOpen(false)
  }

  return (
    <div className={styles.canvasWrapper}>
      {deletedNotice && (
        <div className={styles.noticeSlot}>
          <Alert
            severity="info"
            raised
            message={
              <span className={styles.notice}>
                {t('WEBSITE.CANVAS.ROW_DELETED')}
                {/*
                  A bordered button, not a word in the sentence. Undo is the
                  only thing to do here and it stops being possible after ten
                  seconds, so it has to look like something to press.
                */}
                <Button
                  label={t('WEBSITE.CANVAS.UNDO_DELETE')}
                  variant="outlined"
                  color="primary"
                  icon="las la-undo"
                  smallFont
                  onClick={() => {
                    undo()
                    setDeletedNotice(false)
                  }}
                />
              </span>
            }
          />
        </div>
      )}

      <div className={styles.canvasWell}>
        {/*
          The app's overlay scrollbar, the same one the roster and the calendars
          use. It draws over the content instead of beside it, so it cannot push
          the page sideways when it widens under the pointer.
        */}
        {!dialogOpen && <OverlayScrollbar targetRef={scrollRef} />}
        <div className={styles.canvasScroll} ref={scrollRef}>
          {/*
          Laid out at a real device width and then scaled down to fit. The site's
          stylesheet asks about THIS element's width with container queries, so
          the phone preview is genuinely the phone layout and the desktop preview
          is genuinely the desktop one, whatever room the editor happens to have.
        */}
          <div
            /*
            The theme class belongs here and not only in the prerender. Without
            it the canvas has no `--mp-*` custom properties at all: the page drew
            with no background and no text colour, inheriting the panel's white
            on the dark tile, and the container queries had no container to ask
            about.
          */
            className={`${themeClassName(draft.theme)} ${styles.canvas}`}
            // Computed geometry, which is what an inline style is for. `zoom`
            // rather than `transform: scale`, because zoom keeps the element in
            // the document flow, so the scroll container still measures its real
            // height and the page does not need a spacer under it.
            style={{
              ...themeStyle(draft.theme),
              width: `${canvasWidth}px`,
              zoom: fitScale
            }}
          >
            <BlockRenderer
              blocks={page.blocks}
              context={context}
              renderRowChrome={renderRowChrome}
              renderItemChrome={renderItemChrome}
              renderColumnExtra={renderColumnExtra}
            />
          </div>
          {/*
          Outside the zoomed page, not inside it. It is the editor's control,
          not part of the document, so it has no business shrinking with the
          preview: at a fit of 0.55 it was drawing at half size.
        */}
          <div className={styles.addRow}>
            <Button
              label={t('WEBSITE.CANVAS.ADD_ROW')}
              variant="contained"
              color="secondary"
              icon="las la-plus"
              onClick={() => setLayoutPickerOpen(true)}
            />
          </div>
        </div>
      </div>

      <Modal
        title={t('WEBSITE.CANVAS.ADD_ROW')}
        size="medium"
        isOpen={layoutPickerOpen}
        onClose={() => setLayoutPickerOpen(false)}
      >
        <p className={`body-medium ${styles.modalHint}`}>
          {t('WEBSITE.CANVAS.ROW_HINT')}
        </p>
        <LayoutPicker onPick={handlePickLayout} />
      </Modal>

      <Modal
        title={t('WEBSITE.CANVAS.ADD_ELEMENT')}
        size="medium"
        isOpen={paletteRow !== null}
        onClose={() => setPaletteTarget(null)}
      >
        <p className={`body-medium ${styles.modalHint}`}>
          {t('WEBSITE.CANVAS.ELEMENT_HINT')}
        </p>
        {paletteRow && paletteTarget && (
          <ElementPalette
            row={paletteRow}
            columnId={paletteTarget.columnId}
            onPick={handlePickElement}
          />
        )}
      </Modal>
    </div>
  )
}

export default EditorCanvas
